import { SummaryCSVData, SummaryData } from './types'
import React, { useCallback, useMemo } from 'react'
import { useAtomValue, useSetAtom } from 'infrastructure/state/jotai.wrapper'
import {
  currentSheetDataAtom,
  summaryMappingAtom,
  treeAtom,
  treeIdMappingAtom,
  updatingAtom,
} from './atoms'
import { openNode } from '../../utility/TreeNavigation'
import { goToBuilder } from '../TreeActions'
import { addNodeSummary } from './utility'
import { idsForNode } from '../nodeUpdates/utility'
import { Button } from '@mui/material'
import { doInBatches } from 'shared/utils'
import { useGetSheetTab, useGetSpreadsheet } from '../googleApis/drive'
import { parseCsvDataWithHeaders } from '../../Helpers'

export const spreadSheetId = '13M74BvrwhG247UhwqyxCkR8rajQkGqtuQ7JBMQbLj08'

export function useOpenKiddom() {
  const idMap = useAtomValue(treeIdMappingAtom)
  return useCallback(
    (nodeId: string) => {
      if (!idMap) return
      const { node } = idMap[nodeId]
      openNode(node, idMap)
    },
    [idMap],
  )
}

export function useOpenAdmin() {
  const idMap = useAtomValue(treeIdMappingAtom)
  return useCallback(
    (nodeId: string) => {
      if (!idMap) return
      const { node } = idMap[nodeId]
      goToBuilder(idsForNode(node))
    },
    [idMap],
  )
}

function useDoAddMapping() {
  const idMap = useAtomValue(treeIdMappingAtom)
  return useCallback(
    async ([nodeId, data]: [string, SummaryData[]]) => {
      if (!idMap) return
      const { node } = idMap[nodeId]
      await addNodeSummary(node, data)
    },
    [idMap],
  )
}

export function useAddMapping() {
  const updater = useDoAddMapping()
  const setUpdating = useSetAtom(updatingAtom)
  const { resetTree } = useAtomValue(treeAtom)
  return useCallback(
    async (params: [string, SummaryData[]]) => {
      setUpdating(true)
      await updater(params)
      resetTree?.()
      setUpdating(false)
    },
    [resetTree, setUpdating, updater],
  )
}

export function useUpdateAll() {
  const updater = useDoAddMapping()
  const setUpdating = useSetAtom(updatingAtom)
  const { resetTree } = useAtomValue(treeAtom)
  const mappings = useAtomValue(summaryMappingAtom)
  return useCallback(async () => {
    if (!mappings) return
    setUpdating(true)
    await doInBatches(Object.entries(mappings.mapped), updater)
    resetTree?.()
    setUpdating(false)
  }, [updater, mappings, setUpdating, resetTree])
}

export function useOpenKiddomColumn() {
  const openKiddom = useOpenKiddom()
  return useMemo(() => {
    return {
      title: 'Open Kiddom',
      width: 200,
      rowContent: ([nodeId]: [string, SummaryData[]]) => {
        return <Button onClick={() => openKiddom(nodeId)}>Kiddom</Button>
      },
    }
  }, [openKiddom])
}

export function useOpenAdminColumn() {
  const openAdmin = useOpenAdmin()
  return useMemo(() => {
    return {
      title: 'Open Admin',
      width: 200,
      rowContent: ([nodeId]: [string, SummaryData[]]) => {
        return <Button onClick={() => openAdmin(nodeId)}>Admin</Button>
      },
    }
  }, [openAdmin])
}

export function useSummaryDataForCurrentSheet() {
  const dataSet = useSetAtom(currentSheetDataAtom)
  const sheetFetch = useGetSheetTab()
  return useCallback(
    async (tabId?: string) => {
      if (!tabId) {
        dataSet(undefined)
        return
      }
      const sheetData = await sheetFetch(spreadSheetId, tabId)
      if (!sheetData) {
        dataSet(undefined)
        return
      }
      const data = await parseCsvDataWithHeaders<SummaryCSVData>(sheetData)
      dataSet(data)
    },
    [sheetFetch, dataSet],
  )
}

export function useSheetIds() {
  const getSheet = useGetSpreadsheet()
  return useCallback(async () => {
    const sheet = await getSheet(spreadSheetId)
    if (!sheet) return []
    return sheet.sheets.map(({ properties: { sheetId, title } }) => {
      return { sheetId, title }
    })
  }, [getSheet])
}
